import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ApplyRewardFailureCode, ValidateRewardFailureCode } from '@sg/graphql-schema';

import { type ApplyRewardGenericErrorCode } from '../useRewardErrorMessage.constants';
import {
  daysOfTheWeekMessages,
  fallbackMessages,
  genericMessages,
} from '../useRewardErrorMessage.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useFallbackRewardGenericErrorMessages = () => {
  const { formatMessage: fm } = useIntl();

  const {
    REWARD_MULTIPLE_ERRORS,
    NETWORK_ERROR,
    UNAUTHORIZED_ORDER_MODIFICATION,
    ORDER_LOCKED,
    CART_NOT_FOUND,
    REWARD_NOT_REMOVED,
  } = genericMessages;

  return useMemo<RewardGenericErrorCodeMessages>(
    () => ({
      REWARD_MULTIPLE_ERRORS: fm(REWARD_MULTIPLE_ERRORS),
      NETWORK_ERROR: fm(NETWORK_ERROR),
      UNAUTHORIZED_ORDER_MODIFICATION: fm(UNAUTHORIZED_ORDER_MODIFICATION),
      ORDER_LOCKED: fm(ORDER_LOCKED),
      CART_NOT_FOUND: fm(CART_NOT_FOUND),
      REWARD_NOT_REMOVED: fm(REWARD_NOT_REMOVED),
    }),
    [
      fm,
      REWARD_MULTIPLE_ERRORS,
      NETWORK_ERROR,
      UNAUTHORIZED_ORDER_MODIFICATION,
      ORDER_LOCKED,
      CART_NOT_FOUND,
      REWARD_NOT_REMOVED,
    ],
  );
};

export const useFallbackRewardErrorMessagesByCode = () => {
  const { formatMessage: fm } = useIntl();

  const {
    REWARD_NOT_APPLICABLE,
    RESTAURANT_NOT_ACCEPTING_ORDERS,
    REWARD_EXPIRED,
    REWARD_NOT_FOUND,
  } = fallbackMessages;

  return useMemo<ApplyRewardFailureCodeMessages>(
    () => ({
      [ApplyRewardFailureCode.LoyaltyNotProcessed]: fm(REWARD_NOT_APPLICABLE),
      [ApplyRewardFailureCode.RewardExpired]: fm(REWARD_EXPIRED),
      [ApplyRewardFailureCode.RewardNotApplicable]: fm(REWARD_NOT_APPLICABLE),
      [ApplyRewardFailureCode.RewardNotFound]: fm(REWARD_NOT_FOUND),
      [ApplyRewardFailureCode.LoyaltyProgramUnavailable]: fm(REWARD_NOT_APPLICABLE), // prettier-ignore
      [ApplyRewardFailureCode.RestaurantNotAcceptingOrders]: fm(RESTAURANT_NOT_ACCEPTING_ORDERS), // prettier-ignore
    }),
    [fm, REWARD_NOT_APPLICABLE, REWARD_EXPIRED, REWARD_NOT_FOUND, RESTAURANT_NOT_ACCEPTING_ORDERS],
  );
};

export const useFallbackRewardErrorMessagesByReason = () => {
  const { formatMessage: fm } = useIntl();

  const {
    CHANNEL_RESTRICTION,
    DAY_RESTRICTION,
    MISSING_SKU,
    PRICE_MAXIMUM,
    PRICE_MINIMUM,
    RESTAURANT_NOT_ACCEPTING_ORDERS,
    REWARD_EXPIRED,
    REWARD_NOT_APPLICABLE,
    REWARD_NOT_FOUND,
    TIME_RESTRICTION,
  } = fallbackMessages;

  return useMemo<ValidateRewardFailureCodeMessages>(
    () => ({
      [ValidateRewardFailureCode.CartNotFound]: fm(REWARD_NOT_APPLICABLE),
      [ValidateRewardFailureCode.ChannelRestriction]: fm(CHANNEL_RESTRICTION),
      [ValidateRewardFailureCode.DayRestriction]: fm(DAY_RESTRICTION),
      [ValidateRewardFailureCode.MissingSku]: fm(MISSING_SKU),
      [ValidateRewardFailureCode.PriceMaximum]: fm(PRICE_MAXIMUM),
      [ValidateRewardFailureCode.PriceMinimum]: fm(PRICE_MINIMUM),
      [ValidateRewardFailureCode.RewardExpired]: fm(REWARD_EXPIRED),
      [ValidateRewardFailureCode.RewardNotFound]: fm(REWARD_NOT_FOUND),
      [ValidateRewardFailureCode.TimeRestriction]: fm(TIME_RESTRICTION),
      [ValidateRewardFailureCode.LoyaltyLocationNotFound]: fm(REWARD_NOT_APPLICABLE), // prettier-ignore
      [ValidateRewardFailureCode.LoyaltyNotProcessed]: fm(REWARD_NOT_APPLICABLE), // prettier-ignore
      [ValidateRewardFailureCode.LoyaltyProgramUnavailable]: fm(REWARD_NOT_APPLICABLE), // prettier-ignore
      [ValidateRewardFailureCode.RestaurantNotAcceptingOrders]: fm(RESTAURANT_NOT_ACCEPTING_ORDERS), // prettier-ignore
      [ValidateRewardFailureCode.RewardNotApplicable]: fm(REWARD_NOT_APPLICABLE), // prettier-ignore
    }),
    [
      fm,
      CHANNEL_RESTRICTION,
      DAY_RESTRICTION,
      MISSING_SKU,
      PRICE_MAXIMUM,
      PRICE_MINIMUM,
      REWARD_EXPIRED,
      REWARD_NOT_FOUND,
      TIME_RESTRICTION,
      RESTAURANT_NOT_ACCEPTING_ORDERS,
      REWARD_NOT_APPLICABLE,
    ],
  );
};

/**
 * NOTE: Loyalty Vendor days are offset by one (0-6, 0=Monday, 6=Sunday).
 */
export const useDaysOfTheWeekMessages = () => {
  const { formatMessage: fm } = useIntl();

  return useMemo<Record<number, string>>(
    () => ({
      0: fm(daysOfTheWeekMessages.monday),
      1: fm(daysOfTheWeekMessages.tuesday),
      2: fm(daysOfTheWeekMessages.wednesday),
      3: fm(daysOfTheWeekMessages.thursday),
      4: fm(daysOfTheWeekMessages.friday),
      5: fm(daysOfTheWeekMessages.saturday),
      6: fm(daysOfTheWeekMessages.sunday),
    }),
    [fm],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RewardGenericErrorCodeMessages = Record<ApplyRewardGenericErrorCode | 'REWARD_NOT_REMOVED', string>; // prettier-ignore

type ApplyRewardFailureCodeMessages = Record<ApplyRewardFailureCode, string>;

type ValidateRewardFailureCodeMessages = Record<ValidateRewardFailureCode, string>;
